import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/common/Input';
import { Button } from '../components/common/Button';
import { useTranslation } from 'react-i18next';
import GoogleLoginButton from '../components/GoogleLoginButton';
import { COLORS } from '../constants/colors';

const AuthContainer = styled.div`
  max-width: 400px;
  margin: 0 auto;
  padding: 24px;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: 48px;
`;

const AuthHeader = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const AuthTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #212529;
  margin: 0 0 8px 0;
`;

const AuthSubtitle = styled.p`
  font-size: 1rem;
  color: #495057;
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ErrorMessage = styled.div`
  color: #fa5252;
  background-color: #fff5f5;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 0.875rem;
`;

const AuthFooter = styled.div`
  margin-top: 24px;
  text-align: center;
  font-size: 0.875rem;
  color: #495057;
  
  a {
    color: ${COLORS.PRIMARY};
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ForgotPasswordLink = styled.div`
  text-align: right;
  margin-top: -8px;
  margin-bottom: 8px;
  
  a {
    color: ${COLORS.PRIMARY};
    text-decoration: none;
    font-size: 0.875rem;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

interface LocationState {
  from?: string;
}

export const LoginPage: React.FC = () => {
  const { login, isLoggedIn, error: authError, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  
  // Get the redirect path from location state if available
  const { from } = (location.state as LocationState) || { from: '/' };
  
  // Redirect if already logged in
  useEffect(() => {
    if (isLoggedIn) {
      navigate(from || '/');
    }
  }, [isLoggedIn, navigate, from]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Simple validation
    if (!email.trim()) {
      setError(t('login.validationErrors.emailRequired'));
      return;
    }
    
    if (!password) {
      setError(t('login.validationErrors.passwordRequired'));
      return;
    }
    
    const success = await login(email, password);
    
    if (success) {
      navigate(from || '/');
    }
  };
  
  return (
    <AuthContainer>
      <AuthHeader>
        <AuthTitle>{t('login.title')}</AuthTitle>
        <AuthSubtitle>{t('login.subtitle')}</AuthSubtitle>
      </AuthHeader>

      <GoogleLoginButton></GoogleLoginButton>
      
      {(error || authError) && (
        <ErrorMessage>
          {error || authError}
        </ErrorMessage>
      )}
      
      <Form onSubmit={handleSubmit}>
        <Input
          label="Email"
          type="email"
          placeholder={t('login.emailPlaceholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        
        <Input
          label={t('login.password')}
          type="password"
          placeholder={t('login.password')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <ForgotPasswordLink>
          <Link to="/forgot-password">Forgot your password?</Link>
        </ForgotPasswordLink>
        
        <Button
          type="submit"
          variant="primary"
          isFullWidth
          isLoading={loading}
        >
          {t('login.signInButton')}
        </Button>
      </Form>
      
      <AuthFooter>
        {t('login.noAccount')} <Link to="/register">{t('login.signUp')}</Link>
      </AuthFooter>

    </AuthContainer>
  );
};